import type { CancelToken } from 'axios';
import { CommonUtility } from '../common';
import { APIPath } from '../constants';
import { BaseService } from './base';

interface TrackingListParams {
  page?: number;
  limit?: number;
  category?: 'alerts' | 'ignored' | 'in_process_of_delivery';
  carrier?: string;
  search?: string;
  acknowledged?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: any;
}

interface AcknowledgeData {
  note?: string;
}

interface TrackingResponse {
  data: any[];
  totalCount: number;
  totalPages: number;
  page: number;
  limit: number;
}

interface TrackingDetailResponse {
  success: boolean;
  data: any;
}

interface AcknowledgeResponse {
  success: boolean;
  message: string;
  data: {
    trackingNumber: string;
    carrier: string;
    acknowledgedBy: string;
    acknowledgedAt: string;
    acknowledgedNote?: string;
  };
}

class Tracking {
  /**
   * Get list of tracking records
   * @param params - Query parameters (page, limit, category, carrier, search, etc.)
   * @returns Promise with tracking list
   */
  getTrackingList = (
    params: TrackingListParams,
    cancelToken: CancelToken,
  ): Promise<TrackingResponse> => {
    const queryString = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.ordersTracking}?${queryString}`,
      false,
      true,
      cancelToken,
    );
  };

  /**
   * Get tracking data for specific tracking number
   * @param carrier - Carrier code
   * @param trackingNumber - Tracking number
   * @returns Promise with tracking details
   */
  getTrackingData = (
    carrier: string,
    trackingNumber: string,
  ): Promise<TrackingDetailResponse> => {
    return BaseService.get(`${APIPath.ordersTracking}/${carrier}/${trackingNumber}`);
  };

  /**
   * Acknowledge tracking alert
   * @param carrier - Carrier code
   * @param trackingNumber - Tracking number
   * @param data - Request body with optional note
   * @returns Promise with acknowledge response
   */
  acknowledgeTrackingAlert = (
    carrier: string,
    trackingNumber: string,
    data: AcknowledgeData,
  ): Promise<AcknowledgeResponse> => {
    return BaseService.post(
      `${APIPath.ordersTracking}/${carrier}/${trackingNumber}/acknowledge`,
      data,
    );
  };
}

const TrackingService = new Tracking();
Object.freeze(TrackingService);
export { TrackingService };
export type { TrackingListParams, AcknowledgeData, TrackingResponse };
